/* eslint-disable react/destructuring-assignment */
import React from 'react'
import styled from 'styled-components'
import { Box } from 'pcln-design-system'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

// Element that creates the shadow effect style if enabled, or render an empty div if not
const ShadowEffectElement = styled(Box)`
  z-index: 10;
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 24, 51, 0.5);
  pointer-events: none;
`

type ShadowEffectProps = {
  forProperty: string
  enabled?: boolean
  alternativeBlurHandling?: boolean
  children: React.ReactNode
}

type ShadowEffectState = {
  isEnabled: boolean
}

class ShadowEffect extends React.PureComponent<
  ShadowEffectProps & Record<string, unknown>,
  ShadowEffectState
> {
  disableShadowState: () => void

  enableShadowState: () => void

  handleFocus: () => void

  handleBlur: (e: React.FocusEvent) => void

  node?: HTMLDivElement | null

  constructor(props: ShadowEffectProps) {
    super(props)
    this.state = {
      isEnabled: false
    }

    this.disableShadowState = () => this.setState(() => ({ isEnabled: false }))
    this.enableShadowState = () => this.setState(() => ({ isEnabled: true }))

    this.handleFocus = this.enableShadowState
    this.handleBlur = this.props.alternativeBlurHandling
      ? this.handleAlternativeBlur.bind(this)
      : this.disableShadowState
  }

  componentDidMount() {
    if (this.props.alternativeBlurHandling) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      document.addEventListener('focusin', this.handleDocumentClick, true)
      // eslint-disable-next-line @typescript-eslint/unbound-method
      document.addEventListener('click', this.handleDocumentClick, true)
    }
  }

  componentWillUnmount() {
    if (this.props.alternativeBlurHandling) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      document.removeEventListener('focusin', this.handleAlternativeBlur)
      // eslint-disable-next-line @typescript-eslint/unbound-method
      document.removeEventListener('click', this.handleAlternativeBlur)
    }
  }

  handleDocumentClick(e: MouseEvent | FocusEvent) {
    if (e.target && this.node && !this.node.contains(e.target as Element)) {
      this.disableShadowState()
    }
  }

  handleAlternativeBlur(e: any) {
    const target = e.nativeEvent.relatedTarget
    if (!target || !this.node?.contains(target as Element)) {
      this.setState(() => ({ isEnabled: false }))
    }
  }

  render() {
    const { children, ...remainingProps } = this.props
    const { isEnabled } = this.state
    const { handleBlur, handleFocus, enableShadowState, disableShadowState } =
      this
    return (
      <Box
        width={1}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...remainingProps}
      >
        {isEnabled && <ShadowEffectElement />}
        <div
          ref={node => {
            this.node = node
          }}
          style={{
            width: '100%',
            position: 'relative',
            zIndex: isEnabled ? 20 : 'auto'
          }}
        >
          {typeof children === 'function'
            ? children({ isEnabled, enableShadowState, disableShadowState })
            : children}
        </div>
      </Box>
    )
  }
}

type NoShadowEffectProps = {
  children: React.ReactNode
}

const NoShadowEffect = ({ children }: NoShadowEffectProps) => (
  <>
    {typeof children === 'function'
      ? children({
          isEnabled: false,
          enableShadowState: noop,
          disableShadowState: noop
        })
      : children}
  </>
)

const createShadowEffectWhen = (condition: boolean) =>
  condition
    ? (props: ShadowEffectProps) => <ShadowEffect {...props} />
    : (props: NoShadowEffectProps) => <NoShadowEffect {...props} />

export { ShadowEffect as default, NoShadowEffect, createShadowEffectWhen }
